import {useState} from 'react';
import {useParams} from 'react-router-dom';

import {Button, DatePicker, Input, Select} from '@/components';
import {Filter} from '@/components/Filter';
import {
  getCountryOptions,
  getProcessorTxOptions,
  getProcessorTxTypeOptions,
} from '@/constants/formatters';
import {useCountryList} from '@/context/CountryListContext';
import {mapCountryListToOptions} from '@/utils/countries';

import type {ProcessorTxProps} from './types';

const Filters = ({
  filterCount,
  currentFilters,
  filtersToApply,
  clearFilters,
  selectCurrentFilters,
  selectAppliedFilters,
  handleApplyFilters,
}: ProcessorTxProps) => {
  const {transactionId} = useParams();
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const {state: countryListState} = useCountryList();
  const {countryList} = countryListState;

  const countryOptions = mapCountryListToOptions(countryList ?? []);

  const fromDate = filtersToApply.fromDate
    ? new Date(filtersToApply.fromDate)
    : undefined;

  const toDate = filtersToApply.toDate
    ? new Date(filtersToApply.toDate)
    : undefined;

  const handleShowFilterChange = (): void => {
    setShowFilters(!showFilters);
  };

  const handleApplyFilter = (): void => {
    // Only close on success. Dismissing the panel after a rejected date range
    // hid the values the user has to correct. See hooks/useFilters.ts.
    if (handleApplyFilters()) {
      setShowFilters(false);
    }
  };

  const handleClearFilters = (): void => {
    clearFilters();
  };
  return (
    <div className="mt-5 w-full">
      <div className="flex items-end justify-between">
        {/* Filter Box */}
        <div className="flex items-center space-x-3">
          <Filter
            closeFilter={() => setShowFilters(false)}
            handleShowFilter={handleShowFilterChange}
            label="Filters"
            positionLeft={1}
            bodyClassName="w-full sm:w-[600px]"
          >
            {showFilters && (
              <div className="p-4 space-y-4 max-w-4xl mx-auto w-full col-span-full">
                <div className="flex flex-col gap-4">
                  {/* First Row */}
                  <div className="flex flex-col md:flex-row md:items-center gap-2">
                    <DatePicker
                      label="From Date"
                      selectedDate={fromDate}
                      setSelectedDate={(date) =>
                        selectAppliedFilters('fromDate', date)
                      }
                      placeholderText="Select Date"
                    />

                    <DatePicker
                      label="To Date"
                      selectedDate={toDate}
                      setSelectedDate={(date) =>
                        selectAppliedFilters('toDate', date)
                      }
                      placeholderText="Select Date"
                    />

                    <Select
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        selectAppliedFilters('type', e.target.value)
                      }
                      value={filtersToApply.type}
                      name="type"
                      options={getProcessorTxTypeOptions()}
                      placeholder="Select"
                      label="Payment Type"
                      className="w-full md:w-1/3"
                    />
                  </div>

                  {/* Second Row */}
                  <div className="flex flex-col md:flex-row items-center gap-2">
                    {!transactionId && (
                      <Input
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          selectAppliedFilters(
                            'processorTransactionId',
                            e.target.value,
                          )
                        }
                        name="processorTransactionId"
                        label="Processor Transaction ID"
                        type="text"
                        value={filtersToApply.processorTransactionId ?? ''}
                        className="w-full isFull"
                        placeholder="Enter Processor Transaction ID"
                      />
                    )}

                    <Select
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        selectAppliedFilters('country', e.target.value)
                      }
                      value={filtersToApply.country}
                      name="country"
                      options={getCountryOptions(countryOptions)}
                      placeholder="--Select Country--"
                      label="Country"
                      className="w-full md:w-3/5"
                    />
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end">
                  <Button
                    onClick={handleClearFilters}
                    colorScheme="cyan"
                    variant="ghost"
                  >
                    Clear
                  </Button>

                  <Button
                    onClick={handleApplyFilter}
                    colorScheme="cyan"
                    disabled={filterCount === 0}
                    className="ml-3"
                  >
                    Apply Filters {filterCount && ` (${filterCount})`}
                  </Button>
                </div>
              </div>
            )}
          </Filter>
        </div>

        {/* Processor Select Box */}
        <div className="flex items-center space-x-3">
          <Select
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              selectCurrentFilters('processor', e.target.value)
            }
            value={currentFilters.processor}
            name="processor"
            options={getProcessorTxOptions()}
            placeholder="Filter by Processor"
            label="Change View"
          />
        </div>
      </div>
    </div>
  );
};

export default Filters;
